"use client";

import { useState } from "react";
import { motion } from "framer-motion";
import { HiArrowRight } from "react-icons/hi";
import { MdExplore } from "react-icons/md";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { getHomepageLocations } from "@/data/location";
import StayEaseMap from "@/components/shared/StayEaseMap";
import { useLanguage } from "@/lib/i18n";

export default function Location() {
  const { language } = useLanguage();
  const [activeLocationId, setActiveLocationId] = useState<number | null>(null);
  const HOMEPAGE_LOCATIONS = getHomepageLocations(language);

  const content = language === "id" ? {
    tagline: "Prime Bali Location",
    title: "Jantung Strategis Denpasar.",
    subtitle: "Strategis di jantung Renon, semua kebutuhan hidup premium hanya dalam jarak beberapa menit. Klik lokasi untuk melihat rute virtual.",
    backToMain: "Kembali ke Lokasi Utama",
    exploreButton: "Jelajahi Lingkungan Sekitar",
  } : {
    tagline: "Prime Bali Location",
    title: "Strategic Heart of Denpasar.",
    subtitle: "Strategically located in the heart of Renon, all premium living needs are just minutes away. Click a location to view the virtual route.",
    backToMain: "Back to Main Location",
    exploreButton: "Explore Full Neighborhood",
  };

  return (
    <section id="location" className="py-24 md:py-32 bg-white overflow-hidden relative">
      <div className="container mx-auto px-6 md:px-12">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-center">
          {/* LEFT COLUMN: LIST */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="w-full lg:w-1/3 order-2 lg:order-1"
          >
            <span className="text-brand-gold text-xs font-bold tracking-[0.25em] uppercase block mb-4">
              {content.tagline}
            </span>
            <h2 className="font-serif text-4xl md:text-5xl text-brand-charcoal mb-6 leading-tight">
              {content.title}
            </h2>
            <p className="text-gray-500 text-lg font-light mb-10 leading-relaxed">
              {content.subtitle}
            </p>

            <div className="space-y-4 mb-10">
              {HOMEPAGE_LOCATIONS.map((place) => (
                <div
                  key={place.id}
                  onClick={() => setActiveLocationId(place.id)}
                  className={`flex items-center justify-between p-4 rounded-xl cursor-pointer transition-all duration-500 border group ${
                    activeLocationId === place.id
                      ? "bg-brand-charcoal text-white border-brand-charcoal shadow-xl scale-105"
                      : "bg-white border-gray-100 hover:border-brand-gold/50 hover:bg-[#FAF9F6]"
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div
                      className={`${
                        activeLocationId === place.id
                          ? "text-brand-gold"
                          : "text-gray-400 group-hover:text-brand-gold"
                      } transition-colors`}
                    >
                      <place.icon size={18} />
                    </div>
                    <span
                      className={`font-serif text-lg ${
                        activeLocationId === place.id ? "text-white" : "text-brand-charcoal"
                      }`}
                    >
                      {place.name}
                    </span>
                  </div>

                  {activeLocationId === place.id ? (
                    <HiArrowRight size={16} className="text-brand-gold" />
                  ) : (
                    <span className="text-xs font-bold bg-brand-cream text-brand-gold px-3 py-1 rounded-full uppercase tracking-wider group-hover:bg-white border border-transparent group-hover:border-brand-gold/20 transition-all">
                      {place.dist}
                    </span>
                  )}
                </div>
              ))}

              {activeLocationId !== null && (
                <button
                  onClick={() => setActiveLocationId(null)}
                  className="text-xs text-gray-400 hover:text-brand-gold underline mt-4 ml-2 transition-colors"
                >
                  {content.backToMain}
                </button>
              )}
            </div>

            <Link href="/location">
              <Button
                variant="outline"
                className="w-full rounded-none border-brand-charcoal text-brand-charcoal hover:bg-brand-charcoal hover:text-white px-6 py-6 uppercase text-xs font-bold tracking-widest transition-all flex items-center justify-center gap-3"
              >
                {content.exploreButton} <MdExplore size={16} />
              </Button>
            </Link>
          </motion.div>

          {/* RIGHT COLUMN: MAP */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="w-full lg:w-2/3 h-[500px] lg:h-[600px] relative order-1 lg:order-2"
          >
            <StayEaseMap
              locations={HOMEPAGE_LOCATIONS}
              activeLocationId={activeLocationId}
              onLocationSelect={setActiveLocationId}
            />
            <div className="absolute -bottom-6 -right-6 w-40 h-40 border-r-[1px] border-b-[1px] border-brand-gold rounded-br-3xl hidden md:block opacity-60 pointer-events-none" />
            <div className="absolute -top-6 -left-6 w-40 h-40 border-l-[1px] border-t-[1px] border-brand-gold rounded-tl-3xl hidden md:block opacity-60 pointer-events-none" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
